import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import cn from 'classnames';
import { DrugItem } from 'src/api/types';
import NextImage from 'src/shared-components/NextImage/NextImage';
import useGTAG, { GTAGPrefix } from 'src/hooks/useGTAG';
import trns from '../../trns';
import {
  generateDescriptionSpecialties,
  generateDescriptionWhoCanUse,
  generateInstructionsFields
} from '../../utils';
import NavigationLink from '../../../../shared-components/NavigationLink/NavigationLink';
import {
  generateThirdLevelCategoryPageLink,
  generateTradenamePageLink
} from '../../../../utils/links';

interface Props {
  drug: DrugItem;
  thirdCategoryName: string;
  gtagPrefix?: GTAGPrefix;
}

const descriptionBlocks = (drug, thirdCategoryName) => [
  {
    title: trns.specificationsLabels,
    fields: generateInstructionsFields(drug, thirdCategoryName)
  },
  {
    title: trns.whoCanUse,
    fields: generateDescriptionWhoCanUse(drug)
  },
  {
    title: trns.specialties,
    fields: generateDescriptionSpecialties(drug)
  }
];

const DescriptionTabContent: FC<Props> = ({
  drug,
  thirdCategoryName,
  gtagPrefix
}) => {
  const { trackGtagEvent } = useGTAG();

  const getCharacteristics = (field) => {
    if (field.value === drug.tradeName) {
      return (
        <NavigationLink
          passClassName={'underline'}
          href={generateTradenamePageLink({
            drug: drug.tradeNameLink.translitName
          })}
          onClick={() =>
            trackGtagEvent(
              `${gtagPrefix}_characteristics_tradename`,
              drug.tradeName,
              'tradename_name'
            )
          }
        >
          {field?.value}
        </NavigationLink>
      );
    }
    const categories: { name: string; translitName: string }[] = Object.values(
      drug.categories
    );
    const fieldCategory = categories.find(
      (category) => category.name === field.value
    );
    if (fieldCategory) {
      const href = generateThirdLevelCategoryPageLink({
        category: { translitName: categories[0].translitName },
        secondCategoryId: categories[1].translitName,
        thirdCategoryId: categories[2].translitName
      });
      return (
        <NavigationLink
          passClassName={'underline'}
          href={href}
          onClick={() =>
            trackGtagEvent(
              `${gtagPrefix}_characteristics_category`,
              fieldCategory.translitName,
              'category_name'
            )
          }
        >
          {field?.value}
        </NavigationLink>
      );
    }
    return field?.value;
  };

  return (
    <div className="grid grid-cols-3 gap-6">
      {descriptionBlocks(drug, thirdCategoryName).map((block, index) => {
        return (
          block.fields?.length > 0 && (
            <div
              key={index}
              className={cn(
                'col-span-3 bg-wildSand lm:rounded-xl p-6',
                index === 0 && 'lm:col-span-3',
                index === 1 && 'lm:col-span-2',
                index === 2 && 'lm:col-span-1'
              )}
            >
              <p className="text-astronautBlue text-base font-bold uppercase">
                <FormattedMessage {...block.title} />
              </p>
              <div
                className={cn(
                  'grid gap-4 pt-4',
                  index === 0 && 'lm:grid-rows-4 lm:grid-flow-col',
                  index === 1 && 'lm:grid-cols-2'
                )}
              >
                {block.fields.map((field, fieldIndex) => {
                  return field?.image ? (
                    <div key={fieldIndex} className="flex items-center">
                      <NextImage
                        alt={field?.title as string}
                        src={field?.image.src}
                        layout="fixed"
                        width={25}
                        height={25}
                      />
                      <div className="flex-col p-1 ml-4">
                        <p className={cn('text-sm text-shuttleGray pb-1')}>
                          {field?.title}
                        </p>
                        <p
                          className={cn(
                            'text-sm font-bold text-astronautBlue',
                            field?.status === 'GREEN' && 'text-limeade',
                            field?.status === 'ORANGE' && 'text-pizazz',
                            field?.status === 'RED' && 'text-red400'
                          )}
                        >
                          {field?.value}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div key={fieldIndex} className="flex flex-col">
                      <p className={cn('text-sm text-shuttleGray pb-1')}>
                        {field?.title}
                      </p>
                      <p className={cn('text-sm font-bold text-astronautBlue')}>
                        {getCharacteristics(field)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )
        );
      })}
    </div>
  );
};

export default DescriptionTabContent;
